import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Brain,
  ArrowRight,
  Heart,
  MessageSquare,
  Eye,
  Activity,
  Smartphone,
  Globe,
  Download,
  CheckCircle,
  Mail,
  Menu,
  X,
} from "lucide-react";

import "./css/landing.css"

// Imágenes
import heroImage from "./assets/hero-brain.jpg";
import AleImage from "./assets/IMG_ALEJANDRA.jpg";
import XayilImage from "./assets/IMG_XAYIL.jpg";
import AlexImage from "./assets/IMG_ALEX.jpg";
import VictorImage from "./assets/IMG_VICTOR.jpg";
import LogounamImage from "./assets/Logounam.png";
import LogoupImage from "./assets/Logoup.png";

const Landing = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);


  // Hero Section
  const Hero = () => (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-purple-600 pt-16">
      <div className="absolute inset-0 z-0">
        <img
          src={heroImage}
          alt="Neural networks background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/80 via-blue-700/80 to-purple-600/80" />
      </div>

      <div className="container relative z-10 px-4 py-20 max-w-6xl mx-auto">
        <div className="text-center text-white">
          <div className="flex justify-center mb-6 animate-fade-in">
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
              <Brain className="w-6 h-6" />
              <span className="text-sm font-medium">
                Batería Neuropsicológica Afectiva y Social
              </span>
            </div>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight animate-fade-in-up">
            Bienvenido a
            <span className="block bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
              BANAS
            </span>
          </h1>

          <p className="text-xl md:text-2xl mb-10 text-white/90 max-w-3xl mx-auto animate-fade-in-up animation-delay-200">
            Realiza pruebas validadas para medir empatía, cognición social y
            estado de ánimo. Accede a tus resultados completos en cualquier
            momento.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up animation-delay-400">
            <button
              className="bg-white text-blue-600 hover:bg-blue-50 text-lg px-8 py-4 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 group shadow-lg"
              onClick={() => navigate("/register")}
            >
              Regístrate
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              className="bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white hover:bg-white/20 text-lg px-8 py-4 rounded-lg font-semibold transition-all"
              onClick={() => navigate("/login")}
            >
              Iniciar sesión
            </button>
          </div>

          <div className="mt-16 flex flex-wrap justify-center gap-8 text-sm text-white/80 animate-fade-in animation-delay-600">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-white" />
              <span>Resultados Instantáneos</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-white" />
              <span>Acceso Móvil y Web</span>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/40 rounded-full flex justify-center p-2">
          <div className="w-1 h-2 bg-white rounded-full animate-scroll" />
        </div>
      </div>
    </section>
  );

  // Tests Section
  const TestsSection = () => {
    const tests = [
      {
        title: "Test de Empatía",
        description:
          "Mide tu capacidad para comprender y compartir los sentimientos de otros. Esta prueba evalúa la empatía cognitiva y emocional a través de escenarios del mundo real.",
        duration: "10-15 min",
        icon: Heart,
      },
      {
        title: "Hinting Task",
        description:
          "Evalúa tu habilidad para inferir las intenciones reales detrás del habla indirecta. Perfecto para comprender la comunicación social y la teoría de la mente.",
        duration: "8-12 min",
        icon: MessageSquare,
      },
      {
        title: "Reading the Mind in the Eyes",
        description:
          "Prueba tu capacidad para reconocer estados mentales complejos a partir de expresiones faciales. Una medida clave de inteligencia social y percepción emocional.",
        duration: "12-15 min",
        icon: Eye,
      },
      {
        title: "Test de Ansiedad Beck",
        description:
          "Evalúa tu bienestar emocional actual con herramientas de detección validadas. Obtén información sobre niveles de estrés e indicadores de salud mental.",
        duration: "5-10 min",
        icon: Activity,
      },
      {
        title: "Test de Depresión Beck | Yessavage",
        description:
          "Evalúa tu bienestar emocional actual con herramientas de detección validadas. Obtén información sobre niveles de estrés e indicadores de salud mental.",
        duration: "5-10 min",
        icon: Activity,
      },
      {
        title: "FAB (Frontal Assessment Battery)",
        description:
          "Evalúa las funciones ejecutivas y el rendimiento del lóbulo frontal. Mide procesos clave como la conceptualización, la flexibilidad mental, el control motor y la inhibición conductual.",
        duration: "10-15 min",
        icon: Brain,
      },
      {
        title: "índice de Barthel",
        description:
          "Mide el nivel de autonomía en las actividades básicas de la vida diaria (ABVD). Evalúa de forma objetiva la independencia física en tareas como la movilidad, el autocuidado y la alimentación.",
        duration: "5 min",
        icon: Activity,
      },
    ];

    return (
      <section id="tests" className="py-24 bg-gray-50">
        <div className="container px-4 max-w-6xl mx-auto">
          <div className="mb-16 max-w-4xl mx-auto">
            <div
              style={{
                position: "relative",
                width: "100%",
                height: 0,
                paddingTop: "56.25%",
                paddingBottom: 0,
                boxShadow: "0 2px 8px 0 rgba(63,69,81,0.16)",
                marginTop: "1.6em",
                marginBottom: "0.9em",
                overflow: "hidden",
                borderRadius: 8,
                willChange: "transform",
              }}
            >
              <iframe
                title="Video BANAS - Elige tu instrumento"
                loading="lazy"
                style={{
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                  top: 0,
                  left: 0,
                  border: "none",
                  padding: 0,
                  margin: 0,
                }}
                src="https://www.canva.com/design/DAG-I75T830/5zFxuneFoV9L1_zCOdMqQQ/watch?embed"
                allowFullScreen
                allow="fullscreen"
              />
            </div>
            <a
              href="https://www.canva.com/design/DAG-I75T830/5zFxuneFoV9L1_zCOdMqQQ/watch?utm_content=DAG-I75T830&utm_campaign=designshare&utm_medium=embeds&utm_source=link"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-blue-600 text-sm transition-colors"
            >
            </a>
          </div>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              Elige tu instrumento
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Selección de pruebas validadas para medir empatía, cognición social
              y estado de ánimo en adolescentes y adultos
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {tests.map((test, index) => {
              const Icon = test.icon;
              return (
                <div
                  key={test.title}
                  className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all border border-gray-200 group hover:-translate-y-2"
                >
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <Icon className="w-8 h-8 text-white" />
                  </div>

                  <h3 className="text-2xl font-bold mb-3 text-gray-900">
                    {test.title}
                  </h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {test.description}
                  </p>

                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 text-gray-700 text-sm mb-6">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    {test.duration}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    );
  };

  // About Section
  const AboutSection = () => {
    const features = [
      {
        icon: Smartphone,
        title: "App Tableta/IPad",
        description:
          "Realiza pruebas en cualquier lugar con nuestra aplicación intuitiva",
      },
      {
        icon: Globe,
        title: "Plataforma Web",
        description:
          "Accede a tu panel desde cualquier navegador, en cualquier lugar",
      },
      {
        icon: Download,
        title: "Descarga Resultados",
        description:
          "Exporta informes detallados y rastrea tu progreso a lo largo del tiempo",
      },
    ];

    const benefits = [
      "Instrumentos validados por expertos en neurociencias cognitivas",
      "Consulta inmediata de resultados",
      "Enfocado en privacidad y seguridad",
      "Actualizaciones regulares y nuevas pruebas",
      "Metodología respaldada por expertos",
    ];

    return (
      <section id="about" className="py-24 bg-white">
        <div className="container px-4 max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              Accede desde donde sea, cuando sea
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Accede a los resultados de tus pacientes o participantes cuando lo
              necesites
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={feature.title} className="text-center">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center hover:scale-110 transition-transform">
                    <Icon className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-gray-900">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>

          <div className="bg-gray-50 rounded-3xl p-8 md:p-12">
            <h3 className="text-3xl font-bold mb-8 text-center text-gray-900">
              ¿Por Qué Elegir BANAS?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {benefits.map((benefit, index) => (
                <div key={benefit} className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0" />
                  <span className="text-gray-900 text-lg">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  };

  // Team Section
  const TeamSection = () => {
    const team = [
      {
        name: "Víctor Gálvez",
        role: "Jefe de investigación",
        img: VictorImage,
      },
      {
        name: "Alex Yair Lozano",
        role: "Especialista en neuropsicología",
        img: AlexImage,
      },
      {
        name: "Alejandra Escallada Martínez",
        role: "Responsable de diseño UX/UI",
        img: AleImage,
      },
      {
        name: "Xayil Moab Alameda Méndez",
        role: "CI-CD y Back-End",
        img: XayilImage,
      },
    ];

    return (
      <section id="team" className="py-24 bg-white">
        <div className="container px-4 max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              Conoce a Nuestro Equipo
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Nuestro equipo está formado por profesionales apasionados en
              neurociencia, diseño y tecnología.
            </p>
          </div>

          <div className="overflow-x-auto">
            <div className="flex gap-10 px-2 pb-4 justify-center">
              {team.map((member, index) => (
                <div
                  key={member.name}
                  className="flex flex-col items-center min-w-[200px]"
                >
                  <img
                    src={member.img}
                    alt={member.name}
                    className="w-40 h-40 rounded-full object-cover shadow-lg border border-gray-200 object-[center_-0%]"
                  />
                  <h3 className="mt-4 text-xl font-semibold text-gray-900">
                    {member.name}
                  </h3>
                  <p className="text-gray-600">{member.role}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  };

  // Faculties Section
  const FacultiesSection = () => (
    <section id="faculties" className="py-20 bg-gray-50">
      <div className="container px-4 max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
            Nuestras facultades
          </h2>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-center gap-10">
          <img
            src={LogounamImage}
            alt="Logo UNAM"
            className="max-h-44 md:max-h-52 w-auto object-contain"
          />
          <img
            src={LogoupImage}
            alt="Logo UP"
            className="max-h-44 md:max-h-52 w-auto object-contain"
          />
        </div>
      </div>
    </section>
  );


  // Footer
  const Footer = () => {
    const currentYear = new Date().getFullYear();

    return (
      <footer className="bg-gray-900 border-t border-gray-800 py-12">
        <div className="container px-4 max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
                  <Brain className="w-6 h-6 text-white" />
                </div>
                <span className="text-xl font-bold text-white">BANAS</span>
              </div>
              <p className="text-gray-400 max-w-md">
                Plataforma avanzada de pruebas neuropsicológicas para medir
                habilidades cognitivas, inteligencia emocional y cognición social.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-white mb-4">Tests</h3>
              <ul className="space-y-2">
                <li>
                  <a
                    href="#tests"
                    className="text-gray-400 hover:text-blue-400 transition-colors"
                  >
                    Test de Empatía
                  </a>
                </li>
                <li>
                  <a
                    href="#tests"
                    className="text-gray-400 hover:text-blue-400 transition-colors"
                  >
                    Hinting Task
                  </a>
                </li>
                <li>
                  <a
                    href="#tests"
                    className="text-gray-400 hover:text-blue-400 transition-colors"
                  >
                    Mind in Eyes
                  </a>
                </li>
                <li>
                  <a
                    href="#tests"
                    className="text-gray-400 hover:text-blue-400 transition-colors"
                  >
                    Salud Mental
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-white mb-4">Empresa</h3>
              <ul className="space-y-2">
                <li>
                  <a
                    href="#about"
                    className="text-gray-400 hover:text-blue-400 transition-colors"
                  >
                    Nosotros
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="text-gray-400 hover:text-blue-400 transition-colors"
                  >
                    Investigación
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="text-gray-400 hover:text-blue-400 transition-colors"
                  >
                    Política de Privacidad
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="text-gray-400 hover:text-blue-400 transition-colors"
                  >
                    Contacto
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-gray-800">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-400 text-sm">
                © {currentYear} BANAS. Todos los derechos reservados.
              </p>
              <div className="flex gap-6">
                <a
                  href="#"
                  className="text-gray-400 hover:text-blue-400 transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                  </svg>
                </a>
                <a
                  href="#"
                  className="text-gray-400 hover:text-blue-400 transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      fillRule="evenodd"
                      d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
                      clipRule="evenodd"
                    />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    );
  };

  return (
    <main className="min-h-screen">
      <Hero />
      <TestsSection />
      <AboutSection />
      <TeamSection />
      <FacultiesSection />
      <Footer />
    </main>
  );
};

export default Landing;